import type { FC } from "react";
import { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import type { WidgetProps } from "@rjsf/utils";
import Api from "@/services/Api";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { getLanguageCode, resolvePotentiallyLocalizedString } from "@/utils/obj";
import { useStore } from "@/Store";

import type { DataListRowInternal } from "@/components/DataList/DataListRow";
import type { Document } from "@/declarations/models/Document";
import type PaginationResult from "@/declarations/PaginationResult";
import DataList from "@/components/DataList/DataList";

export interface ReverseRelationsWidgetProps extends WidgetProps {
  value: boolean;
  trueText?: string;
  falseText?: string;
}

const ReverseRelationsWidget: FC<ReverseRelationsWidgetProps> = () => {
  const { documentId } = useParams();
  const { state } = useStore();
  const { t } = useTranslation("common");
  const instance = state.cmsContextInstance;
  const selectedLocale = getLanguageCode(i18next.language);
  const getLocalizedString = resolvePotentiallyLocalizedString(selectedLocale);
  const [lastFetchedTimestamp, setLastFetchedTimestamp] = useState(Date.now());
  const [selectedInstanceId, setSelectedInstanceId] = useState(instance?.id);
  const [sortBy, setSortBy] = useState("name");
  const [sortAscending, setSortAscending] = useState(true);

  const externalDataChanged = useMemo(
    () => ({ lastFetchedTimestamp, selectedInstanceId }),
    [lastFetchedTimestamp, selectedInstanceId]
  );

  const getItems = async (page: number, page_size: number): Promise<PaginationResult<Document>> =>
    await Api.getReverseRelationsOfDocument(parseInt(documentId || "0"), {
      page,
      page_size,
      sort_by_title: sortBy === "name",
      sort_by_internal_title: sortBy === "internal_title",
      sort: ["internal_title", "title"].includes(sortBy) ? undefined : `$${sortBy}`,
      order_asc: sortAscending,
    })
      .fetchDirect({
        page,
        page_size,
        items: [],
        count: 0,
        total_count: 0,
      })
      .then((res: any) => {
        return res;
      });

  function handleOnItemsChanged(sortProp?: string, sortDirection?: string) {
    if (sortProp && sortProp !== sortBy) setSortBy(sortProp);
    if (sortDirection) setSortAscending(sortDirection === "asc");
    setLastFetchedTimestamp(Date.now());
  }

  function mapToDataListItem(doc: any): DataListRowInternal<Document> {
    const document: DataListRowInternal<Document> = {
      key: doc?.id?.toString(),
      title: getLocalizedString(!!doc?.internal_title ? doc.internal_title : doc?.title) ?? "*",
      subTitle: t(doc?.schema_slug ? "documentType." + doc.schema_slug : ""),
      imageURL: doc?.media_data?.thumbnail_url,
      sourceItem: doc,
      updatedAt: doc?.updated_at,
    };
    return document;
  }

  function openReverseRelation(item: any) {
    window.open(`/${item.owner_slug}/${item.owner_id}/${item.id}`, "_blank");
  }

  return (
    <div id="reverseRelationsWidget">
      <DataList
        getItems={getItems}
        mapperFn={mapToDataListItem}
        isInsideModal={true}
        handleOnItemsChanged={handleOnItemsChanged}
        externalDataChanged={externalDataChanged}
        hideHeader
        onItemClick={(item) => openReverseRelation(item)}
      />
    </div>
  );
};

export default ReverseRelationsWidget;
